import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DateField, SelectField, InputField, FileField } from 'vetrf-ui';
import { ConsiderationPeriodDictOption } from 'api/dict/params';
import { EntryRegistrationDTO } from 'api/entryRegistration/types';
import { dictApi } from 'api/dict';
import { EntryType } from 'components/registry/types/api';
import { DocumentFields } from '../DocumentFields';

type FeedFormFieldsProps = {
  entryType: EntryType;
  regData: EntryRegistrationDTO;
  isRegNumberGmoExist: boolean;
};

export const FeedFormFields = ({
  entryType,
  regData,
  isRegNumberGmoExist,
}: FeedFormFieldsProps) => {
  const [considerationPeriodTypeOptions, setConsiderationPeriodTypeOptions] = useState<
    ConsiderationPeriodDictOption[]
  >([]);
  const { reset, setValue } = useFormContext();
  const registryOperationField = useWatch({
    name: 'registryOperation',
  });

  const gehConsiderationPeriodTypeOptions = async () => {
    const res = await dictApi.getConsiderationPeriodTypes();
    setConsiderationPeriodTypeOptions(res);
  };

  useEffect(() => {
    gehConsiderationPeriodTypeOptions();
  }, []);

  useEffect(() => {
    reset({ registryOperation: registryOperationField });
  }, [registryOperationField?.code]);

  if (registryOperationField) {
    switch (registryOperationField.name) {
      case 'Проводится экспертиза кормовой добавки':
        return (
          <SelectField
            labelText="Количество дней рассмотрения"
            name="considerationPeriodType"
            defaultValue={considerationPeriodTypeOptions[0]}
            labelKey="name"
            valueKey="code"
            required="Необходимо заполнить поле"
            options={considerationPeriodTypeOptions}
          />
        );
      case 'Запрос дополнительной информации':
        return (
          <DateField
            dateFormat="dd.MM.yyyy"
            labelText="Дата запроса ВГНКИ"
            locale="ru"
            name="vgnkiRequestDate"
            required="Необходимо заполнить поле"
            validateDateFormat
          />
        );
      case 'Зарегистрировано / Внесено в реестр':
        if (entryType === EntryType.registry) {
          setValue('startValidityDate', regData.startValidityDate);
          setValue('regNumber', regData.regNumber);
          setValue('endValidityDate', regData.endValidityDate);
        }
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата заключения ВГНКИ"
              locale="ru"
              name="vgnkiDecisionDate"
              validateDateFormat
            />
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата регистрации"
              locale="ru"
              name="startValidityDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <InputField
              labelText="Регистрационный номер"
              name="regNumber"
              placeholder="РФ-КД-00000"
              required="Необходимо заполнить поле"
              defaultValue="РФ-КД-"
            />
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Срок государственной регистрации"
              locale="ru"
              name="endValidityDate"
              required={isRegNumberGmoExist && 'Необходимо заполнить поле'}
              validateDateFormat
            />
            <DocumentFields />
          </>
        );
      case 'Регистрация приостановлена':
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата принятия решения"
              locale="ru"
              name="decisionDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Срок приостановления"
              locale="ru"
              name="suspensionPeriodDate"
              validateDateFormat
            />
            <DocumentFields required defaultDocumentType="Приказ Россельхознадзора" />
            <FileField name="file" allowMultiple={false} />
          </>
        );
      case 'Отказано в госрегистрации':
        return (
          <DateField
            dateFormat="dd.MM.yyyy"
            labelText="Дата принятия решения"
            locale="ru"
            name="decisionDate"
            required="Необходимо заполнить поле"
            validateDateFormat
          />
        );
      case 'Внесение изменений в регистрационное досье':
        return <DocumentFields required defaultDocumentType="Заявление" withNumber={false} />;
      case 'Отказ во внесении изменений в регистрационное досье':
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата принятия решения"
              locale="ru"
              name="decisionDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <DocumentFields required />
          </>
        );
      case 'Внесены изменения в регистрационное досье на зарегистрированную кормовую добавку':
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата внесения изменений в досье"
              locale="ru"
              name="changeDossierDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <DocumentFields />
          </>
        );
      case 'Регистрация отменена':
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата отмены регистрации"
              locale="ru"
              name="deregistrationDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <DocumentFields required />
          </>
        );
      case 'Возобновление государственной регистрации':
        return (
          <>
            <DateField
              dateFormat="dd.MM.yyyy"
              labelText="Дата возобновления"
              locale="ru"
              name="renewRegistrationDate"
              required="Необходимо заполнить поле"
              validateDateFormat
            />
            <DocumentFields required />
            <FileField name="file" allowMultiple={false} />
          </>
        );
      case 'Представлен ответ на запрос дополнительной информации':
        return (
          <DateField
            dateFormat="dd.MM.yyyy"
            labelText="Дата ответа на запрос"
            locale="ru"
            name="additionalInformationDate"
            required="Необходимо заполнить поле"
            validateDateFormat
          />
        );
      default:
        return null;
    }
  }
  return null;
};
